import type { NostrEvent } from '@nostrify/nostrify';
import type { Merchant } from './merchants';

/**
 * BCHNostr merchant *directory* listings.
 *
 * Separate from the kind 30499 "professional profile" (see lib/business.ts),
 * BCHNostr's directory (bchnostr.com/merchants) also lets anyone publish a
 * lightweight paid listing via its "Add a listing" form. Those are stored as:
 *
 *   kind: 30023                          // NIP-23 long-form kind, reused
 *   tags:
 *     ["t", "bch-merchant"]              // discovery marker
 *     ["t", "bch-merchant-featured"]     // optional, if the listing is featured
 *     ["d", "merchant-<timestamp>"]
 *     ["title", "<business name>"]
 *   content: JSON {
 *     title, description, category, location, website, featured
 *   }
 *
 * These never appeared on BCH Local before because the app only imported
 * kind 30499 professional profiles and kind 35341 BCHPAY listings. A business
 * that listed itself through the directory form (e.g. "Forward Financial, LLC")
 * was therefore visible on bchnostr.com but invisible here. This module maps
 * those events into the app's `Merchant` shape so they show up too.
 *
 * NOTE: kind 30023 is the generic NIP-23 article kind used by many apps for
 * blog posts. We ONLY treat an event as a directory listing when it carries the
 * `bch-merchant` `t` tag AND its content is a JSON object with a usable name —
 * never a raw markdown article.
 */

/** Kind reused by BCHNostr directory listings (NIP-23 long-form). */
export const DIRECTORY_KIND = 30023;
/** Discovery marker tag every directory listing carries. */
export const DIRECTORY_TAG = 'bch-merchant';
/** Marker for a featured (paid-boost) directory listing. */
export const DIRECTORY_FEATURED_TAG = 'bch-merchant-featured';

/**
 * Map BCHNostr's directory category *labels* to this app's category IDs.
 * The directory form stores human labels (e.g. "Food & Drink"), not ids.
 */
const DIRECTORY_CATEGORY_MAP: Record<string, string> = {
  'food & drink': 'restaurant',
  food: 'restaurant',
  retail: 'retail',
  services: 'services',
  online: 'other',
  entertainment: 'entertainment',
  technology: 'services',
  other: 'other',
};

/** Shape of the JSON content field on a kind 30023 directory listing. */
interface DirectoryContent {
  title?: string;
  description?: string;
  category?: string;
  location?: string;
  website?: string;
  featured?: boolean;
}

/** Get a single tag value from an event. */
function getTag(event: NostrEvent, name: string): string {
  return event.tags.find(([n]) => n === name)?.[1] ?? '';
}

/**
 * Whether an event is a BCHNostr directory listing (not a generic NIP-23
 * article). Requires the `bch-merchant` marker and JSON content with a name.
 */
export function isDirectoryListing(event: NostrEvent): boolean {
  if (event.kind !== DIRECTORY_KIND) return false;
  const hasMarker = event.tags.some(([n, v]) => n === 't' && v === DIRECTORY_TAG);
  if (!hasMarker) return false;
  // Must have a title tag or JSON content — a raw markdown article is not a listing.
  const titleTag = getTag(event, 'title');
  if (titleTag.trim()) return true;
  try {
    const parsed = JSON.parse(event.content) as DirectoryContent;
    return typeof parsed === 'object' && parsed !== null && !!parsed.title?.trim();
  } catch {
    return false;
  }
}

/**
 * Parse a BCHNostr kind 30023 directory listing into the app's `Merchant`
 * shape. Returns null for events that aren't directory listings.
 *
 * These listings carry only free-text `location` (no geohash), so they have no
 * map coordinates and appear in the list / "online only" surfaces rather than
 * as a map pin — which is the correct behaviour for the location-less
 * businesses that use this form.
 */
export function parseDirectoryListing(event: NostrEvent): Merchant | null {
  if (!isDirectoryListing(event)) return null;

  let content: DirectoryContent = {};
  try {
    content = JSON.parse(event.content) as DirectoryContent;
  } catch {
    content = {};
  }

  const name = getTag(event, 'title') || content.title || '';
  if (!name.trim()) return null;

  const rawCategory = (content.category ?? '').trim().toLowerCase();
  const mappedCategory = rawCategory
    ? DIRECTORY_CATEGORY_MAP[rawCategory] ?? 'other'
    : '';
  const categories = mappedCategory ? [mappedCategory] : [];

  // Summary: prefer the description, then the free-text location.
  const summary = (content.description || content.location || '').trim();

  const website = typeof content.website === 'string' ? content.website.trim() : '';

  return {
    event,
    pubkey: event.pubkey,
    name: name.trim(),
    summary,
    categories,
    website,
    // Directory listings have no geohash → no map coordinates.
    geohash: '',
    location: null,
    lastActive: null,
    verified: false,
    sponsored: false,
    source: 'bchnostr',
  };
}
